import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from './ui';

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];
const JOURS = ['lu', 'ma', 'me', 'je', 've', 'sa', 'di'];
const HEURES = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const pad = (n: number) => String(n).padStart(2, '0');
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Calendrier + heure au thème du BO (le calendrier natif de `datetime-local` n'est pas stylable).
 * Semaine du lundi au dimanche, heure locale du navigateur, minutes par pas de 5.
 */
export default function DateTimePicker({
  value,
  onChange,
  today,
}: {
  value: Date;
  onChange: (date: Date) => void;
  /** « Aujourd'hui » au sens de l'app = heure serveur (décalable). */
  today: Date;
}) {
  const [mois, setMois] = useState(() => new Date(value.getFullYear(), value.getMonth(), 1));

  // Le mois affiché suit la valeur quand elle change de mois (ex. clic sur une chip H-48).
  const [suivi, setSuivi] = useState(`${value.getFullYear()}-${value.getMonth()}`);
  const cle = `${value.getFullYear()}-${value.getMonth()}`;
  if (cle !== suivi) {
    setSuivi(cle);
    setMois(new Date(value.getFullYear(), value.getMonth(), 1));
  }

  const decalage = (mois.getDay() + 6) % 7; // lundi = 0
  const nbJours = new Date(mois.getFullYear(), mois.getMonth() + 1, 0).getDate();
  const cases: (Date | null)[] = [
    ...Array.from({ length: decalage }, () => null),
    ...Array.from({ length: nbJours }, (_, i) => new Date(mois.getFullYear(), mois.getMonth(), i + 1)),
  ];

  const choisirJour = (jour: Date) => {
    const d = new Date(value);
    d.setFullYear(jour.getFullYear(), jour.getMonth(), jour.getDate());
    onChange(d);
  };
  const choisirHeure = (h: number, m: number) => {
    const d = new Date(value);
    d.setHours(h, m, 0, 0);
    onChange(d);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-card-border bg-surface-inset p-3">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            aria-label="Mois précédent"
            className="rounded p-1 text-outline hover:bg-white/10 hover:text-white"
            onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}
          >
            <ChevronLeft size={18} aria-hidden />
          </button>
          <p className="text-sm font-semibold capitalize text-white" aria-live="polite">
            {MOIS[mois.getMonth()]} {mois.getFullYear()}
          </p>
          <button
            type="button"
            aria-label="Mois suivant"
            className="rounded p-1 text-outline hover:bg-white/10 hover:text-white"
            onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}
          >
            <ChevronRight size={18} aria-hidden />
          </button>
        </div>
        <div role="grid" aria-label="Calendrier" className="grid grid-cols-7 gap-1 text-center text-sm">
          {JOURS.map((j) => (
            <span key={j} className="py-1 text-xs uppercase text-outline">
              {j}
            </span>
          ))}
          {cases.map((jour, i) =>
            jour ? (
              <button
                key={i}
                type="button"
                aria-label={jour.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                aria-pressed={sameDay(jour, value)}
                onClick={() => choisirJour(jour)}
                className={`rounded-md py-1.5 transition-colors ${
                  sameDay(jour, value)
                    ? 'bg-primary font-semibold text-white'
                    : sameDay(jour, today)
                      ? 'text-white ring-1 ring-primary-container hover:bg-white/10'
                      : 'text-on-surface-variant hover:bg-white/10'
                }`}
              >
                {jour.getDate()}
              </button>
            ) : (
              <span key={i} />
            ),
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold text-on-surface-variant">Heure</span>
        <Select
          aria-label="Heure"
          className="w-20"
          value={value.getHours()}
          onChange={(e) => choisirHeure(Number(e.target.value), value.getMinutes())}
        >
          {HEURES.map((h) => (
            <option key={h} value={h}>
              {pad(h)}
            </option>
          ))}
        </Select>
        <span className="text-outline">:</span>
        <Select
          aria-label="Minutes"
          className="w-20"
          value={value.getMinutes() - (value.getMinutes() % 5)}
          onChange={(e) => choisirHeure(value.getHours(), Number(e.target.value))}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {pad(m)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
